import { useFormContext } from "react-hook-form";
import { useTranslationApp } from "../../../../lib/i18next";
import {
  ADS_CALL_TO_ACTION_SEND_MESSAGE_PAGE_META_VALUE,
  ADS_CAMPAIGN_META_DESTINATION_TYPE_LEAD_INSTANT_FORM_OPTION,
  ADS_CAMPAIGN_META_DESTINATION_TYPE_ON_POST_OPTION,
  ADS_CAMPAIGN_META_DESTINATION_TYPE_WEBSITE_OPTION,
  ADS_CAMPAIGN_META_DESTINATION_TYPES_WEBSITE,
  ADS_CAMPAIGN_META_DESTINATION_TYPES_WITH_OFFLINE_TRACKING_ID,
  ADS_CAMPAIGN_META_DESTINATION_TYPES_WITH_TRACKING_ID,
  ADS_CAMPAIGN_OBJECTIVE_LEADS_OPTION,
  ADS_CAMPAIGN_OBJECTIVES_WITH_TRACKING_META,
  ADS_CAMPAIGN_PLATFORM_DESCRIPTION_MAX_LENGTH,
  ADS_CAMPAIGN_PLATFORM_HEADLINE_MAX_LENGTH,
  ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH,
  ADS_CAMPAIGN_STATUS_DRAFT,
  IMAGE_FORMATS,
} from "../../../../utils/constants";
import {
  getShowDestinationTypeAd,
  getShowSiteLinksAdMeta,
} from "../../../../services/adsCampaigns";
import { getIsMessageTemplateRequired } from "../../../../services/meta";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CardContainer from "../../../Card/CardContainer";
import {
  ADS_CAMPAIGN_PREVIEW,
  ADS_CONFIGURATION_DESTINATION_TITLE,
  CALL_TO_ACTION_PLACEHOLDER,
  CALL_TO_ACTIONS_PLACEHOLDER,
  CAROUSEL,
  CONTENT,
  COPY_VALUES_IN_OTHER_ADS,
  CREATIVE_TYPE_PLACEHOLDER,
  DESCRIPTION_PLACEHOLDER,
  DESCRIPTIONS,
  DESTINATION_URL_PLACEHOLDER,
  HEADLINE_PLACEHOLDER,
  HEADLINES,
  INDIVIDUAL,
  INSTANT_FORM_PLACEHOLDER,
  SITE_LINKS_PLACEHOLDER,
  TITLE_PLACEHOLDER,
  TITLES_META,
  TRACKING_EVENTS,
  TRACKING_OFFLINE_EVENTS_PLACEHOLDER,
  TRACKING_WEBSITE_EVENTS,
  UTM_PARAMETERS_PLACEHOLDER,
} from "../../../../i18n/keysTranslations";
import Select from "../../../Form/Select";
import ContentSelectorDynamicMeta from "./ContentSelectorDynamicMeta";
import TextField from "../../../Form/TextField";
import ClickableText from "../../../Texts/ClickableText";
import AdMetaSiteLinksContainer from "./AdMetaSiteLinksContainer";
import ChipOptional from "../../../Chip/ChipOptional";
import CardContainerWithCopy from "../../../Card/CardContainerWithCopy";
import TextFieldWithActions from "../../../Form/TextFieldWithActions";
import InstantFormContainer from "../../../Meta/InstantFormContainer";
import InstantFormRecommendedQuestionsTrigger from "./InstantFormRecommended/InstantFormRecommendedQuestionsTrigger";
import MessageTemplateContainer from "../../../Meta/MessageTemplateContainer";
import SelectCallToActionMeta from "./SelectCallToActionMeta";
import SelectTrackingIDField from "../../../Form/SelectTrackingIDField";
import EditAdvantageEnhances from "./EditAdvantageEnhances";
import AdPreviewMeta from "./AdPreviewMeta";
import { getExtensionFile } from "../../../../utils/string";
import _ from "lodash";
import UtmParametersContainer from "../../../Common/Utms/UtmParametersContainer";

const maxTitlesPlatform = 5;
const maxHeadlinesPlatform = 5;
const MAX_CALL_TO_ACTIONS = 5;

const AdAdDynamicContentMetaForm = ({
  objective,
  platform,
  platformData,
  breakColumnFormAndPreview,
  BASIC_PATH_AD_GROUP,
  BASIC_PATH,
  onAddText,
  onRemoveText,
  onGenerateText,
  isSpecialAdWithOnlyTitles,
  isSpecialAdWithoutCallToAction,
  isGeneralDisabledFields,
  errors,
  control,
  refPreview,
  onGeneratePreview,
  onCopyValuesInOtherAds,
  showSlideChat,
}) => {
  const { watch, setValue, trigger } = useFormContext();
  const { t } = useTranslationApp();

  const adID = watch(`${BASIC_PATH}.id`);
  const language = watch("language");
  const isCarousel = watch(`${BASIC_PATH}.isCarousel`);
  const mediaUrls = watch(`${BASIC_PATH}.mediaUrls`) || [];
  const mediaData = watch(`${BASIC_PATH}.mediaData`) || [];
  const callToActions = watch(`${BASIC_PATH}.callToActions`) || [];
  const titles = watch(`${BASIC_PATH}.titles`) || [{ value: "" }];
  const headlines = watch(`${BASIC_PATH}.headlines`) || [{ value: "" }];
  const descriptions = watch(`${BASIC_PATH}.descriptions`) || [{ value: "" }];
  const destinationTypeAdGroup = watch(
    `${BASIC_PATH_AD_GROUP}.destinationType`
  );
  const destinationType = watch(`${BASIC_PATH}.destinationType`) || "";
  const status = watch(`${BASIC_PATH}.status`);
  const isAdCreated = status !== ADS_CAMPAIGN_STATUS_DRAFT;

  const isCatalogCampaign = platformData?.isCatalogCampaign || false;
  const maxLengthTitle = ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH[platform];
  const maxLengthHeadline = ADS_CAMPAIGN_PLATFORM_HEADLINE_MAX_LENGTH[platform];
  const maxLengthDescription =
    ADS_CAMPAIGN_PLATFORM_DESCRIPTION_MAX_LENGTH[platform];

  const showDestinationTypeAd = getShowDestinationTypeAd({
    objective,
    destinationTypeAdGroup,
  });
  const showSiteLinksAdMeta = getShowSiteLinksAdMeta({
    objective,
    destinationType: destinationTypeAdGroup,
    destinationTypeAd: destinationType,
  });
  const isDestinationTypeAdWebsite =
    (showDestinationTypeAd &&
      destinationType === ADS_CAMPAIGN_META_DESTINATION_TYPE_WEBSITE_OPTION) ||
    !showDestinationTypeAd;
  const isDestinationTypeWebsite =
    ADS_CAMPAIGN_META_DESTINATION_TYPES_WEBSITE.includes(
      destinationTypeAdGroup
    );
  const showDestinationUrl =
    (isDestinationTypeWebsite && isDestinationTypeAdWebsite) ||
    (destinationTypeAdGroup ===
      ADS_CAMPAIGN_META_DESTINATION_TYPE_ON_POST_OPTION &&
      callToActions?.[0] !== ADS_CALL_TO_ACTION_SEND_MESSAGE_PAGE_META_VALUE);

  const isMessageTemplateRequired = getIsMessageTemplateRequired({
    objective,
    mediaData,
    callToAction: callToActions?.[0],
    destinationType: destinationTypeAdGroup,
  });

  const showTrackingID =
    !(
      ADS_CAMPAIGN_OBJECTIVES_WITH_TRACKING_META.includes(objective) &&
      destinationTypeAdGroup ===
        ADS_CAMPAIGN_META_DESTINATION_TYPE_WEBSITE_OPTION &&
      !isCatalogCampaign
    ) &&
    !ADS_CAMPAIGN_META_DESTINATION_TYPES_WITH_TRACKING_ID.filter(
      (i) => i !== ADS_CAMPAIGN_META_DESTINATION_TYPE_WEBSITE_OPTION
    ).includes(destinationTypeAdGroup);

  const showTrackingOfflineID =
    !ADS_CAMPAIGN_META_DESTINATION_TYPES_WITH_OFFLINE_TRACKING_ID.includes(
      destinationTypeAdGroup
    );

  const shouldShowDestinationConfigCard =
    showDestinationTypeAd || showSiteLinksAdMeta || showDestinationUrl;

  const onChangeIsCarousel = (value) => {
    if (value) {
      let mediaUrlsFiltered = [];
      let mediaDataFiltered = [];
      if (!_.isEmpty(mediaUrls)) {
        mediaUrls.forEach((mediaUrl, index) => {
          const extension = getExtensionFile(mediaUrl);
          if (IMAGE_FORMATS.includes(extension)) {
            mediaUrlsFiltered.push(mediaUrl);
            mediaDataFiltered.push(mediaData[index] || { from: "upload" });
          }
        });
        setValue(`${BASIC_PATH}.mediaUrls`, mediaUrlsFiltered);
      }
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: breakColumnFormAndPreview ? "column" : "row",
        gap: 4,
        width: "100%",
        flexWrap: showSlideChat ? "wrap" : "nowrap",
      }}
    >
      <Grid container spacing={3} sx={{ height: "min-content" }}>
        <Grid item xs={12}>
          <CardContainer
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t(CREATIVE_TYPE_PLACEHOLDER)}
                </Typography>
              </Box>
            }
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Select
                  disabled={isGeneralDisabledFields || isAdCreated}
                  variant="filled"
                  fullWidth
                  actionsChange={(value) => {
                    onChangeIsCarousel(value);
                  }}
                  name={`${BASIC_PATH}.isCarousel`}
                  doTranslate={true}
                  control={control}
                  options={[
                    { value: false, label: INDIVIDUAL },
                    { value: true, label: CAROUSEL },
                  ]}
                />
              </Grid>
            </Grid>
          </CardContainer>
        </Grid>

        <Grid item xs={12}>
          <CardContainer
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t(CONTENT)}
                </Typography>
              </Box>
            }
          >
            <ContentSelectorDynamicMeta
              basicPath={BASIC_PATH}
              adID={adID}
              mode={isCarousel ? "carousel" : "individual"}
              disabled={isGeneralDisabledFields}
              language={language}
              onCallbackSave={() => onGeneratePreview("content")}
            />
          </CardContainer>
        </Grid>

        {shouldShowDestinationConfigCard && (
          <Grid item xs={12}>
            <CardContainer
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t(ADS_CONFIGURATION_DESTINATION_TITLE)}
                  </Typography>
                </Box>
              }
            >
              <Grid container spacing={2}>
                {showDestinationUrl && (
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Typography variant="body1">
                        {t(DESTINATION_URL_PLACEHOLDER)}
                      </Typography>
                      <TextField
                        name={`${BASIC_PATH}.destinationUrls.[0].value`}
                        variant="filled"
                        fullWidth
                        disabled={isGeneralDisabledFields}
                        actionsChange={() => onGeneratePreview()}
                        control={control}
                        errors={errors}
                      />
                      <ClickableText
                        disabled={isGeneralDisabledFields}
                        onClick={() =>
                          onCopyValuesInOtherAds({ type: "destinationUrls" })
                        }
                      >
                        {t(COPY_VALUES_IN_OTHER_ADS)}
                      </ClickableText>
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Typography variant="body1">
                        {t(UTM_PARAMETERS_PLACEHOLDER)}
                      </Typography>
                      <ChipOptional size="small" />
                    </Box>
                    <UtmParametersContainer
                      name={`${BASIC_PATH}.utmParameters`}
                      control={control}
                      errors={errors}
                      disabled={isGeneralDisabledFields}
                      platform={platform}
                      onCopyValuesInOtherAds={() =>
                        onCopyValuesInOtherAds({ type: "utmParameters" })
                      }
                    />
                  </Box>
                </Grid>
                {showSiteLinksAdMeta && (
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      <Typography variant="body1">
                        {t(SITE_LINKS_PLACEHOLDER)}
                      </Typography>
                      <AdMetaSiteLinksContainer
                        name={`${BASIC_PATH}.siteLinks`}
                        filled={true}
                        language={language}
                        fullWidth={true}
                        disabled={isGeneralDisabledFields}
                        control={control}
                        errors={errors}
                      />
                      <ClickableText
                        disabled={isGeneralDisabledFields}
                        onClick={() =>
                          onCopyValuesInOtherAds({ type: "siteLinks" })
                        }
                      >
                        {t(COPY_VALUES_IN_OTHER_ADS)}
                      </ClickableText>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContainer>
          </Grid>
        )}

        {/* Instant Form */}
        {objective === ADS_CAMPAIGN_OBJECTIVE_LEADS_OPTION &&
          destinationTypeAdGroup ===
            ADS_CAMPAIGN_META_DESTINATION_TYPE_LEAD_INSTANT_FORM_OPTION && (
            <Grid item xs={12}>
              <CardContainer
                title={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 1,
                      width: "100%",
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {t(INSTANT_FORM_PLACEHOLDER)}
                    </Typography>
                    <Box sx={{ display: "block" }}>
                      <InstantFormRecommendedQuestionsTrigger
                        platformData={platformData}
                        isGeneralDisabledFields={isGeneralDisabledFields}
                      />
                    </Box>
                  </Box>
                }
              >
                <InstantFormContainer
                  name={`${BASIC_PATH}.instantForm`}
                  integrationID={platformData?.integrationID}
                  pageID={platformData?.pageID}
                  disabled={
                    !platformData?.pageID ||
                    !platformData?.integrationID ||
                    isGeneralDisabledFields
                  }
                  fullWidth
                  control={control}
                  errors={errors}
                />
              </CardContainer>
            </Grid>
          )}

        {isMessageTemplateRequired && (
          <Grid item xs={12}>
            <MessageTemplateContainer
              BASIC_PATH={BASIC_PATH}
              BASIC_PATH_AD_GROUP={BASIC_PATH_AD_GROUP}
              isGeneralDisabledFields={isGeneralDisabledFields}
              onCopyValuesInOtherAds={onCopyValuesInOtherAds}
              control={control}
              errors={errors}
            />
          </Grid>
        )}

        {(showTrackingID || showTrackingOfflineID) && (
          <Grid item xs={12}>
            <CardContainer
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t(TRACKING_EVENTS)}
                  </Typography>
                  <ChipOptional size="small" />
                </Box>
              }
            >
              <Grid container spacing={2}>
                {showTrackingID && (
                  <Grid item xs={12}>
                    <SelectTrackingIDField
                      name={`${BASIC_PATH}.trackingID`}
                      disabled={isGeneralDisabledFields}
                      label={t(TRACKING_WEBSITE_EVENTS)}
                      control={control}
                      errors={errors}
                    />
                  </Grid>
                )}
                {showTrackingOfflineID && (
                  <Grid item xs={12}>
                    <SelectTrackingIDField
                      name={`${BASIC_PATH}.trackingOfflineID`}
                      disabled={isGeneralDisabledFields}
                      label={t(TRACKING_OFFLINE_EVENTS_PLACEHOLDER)}
                      control={control}
                      errors={errors}
                    />
                  </Grid>
                )}
              </Grid>
            </CardContainer>
          </Grid>
        )}

        {!isSpecialAdWithOnlyTitles && (
          <Grid item xs={6}>
            <CardContainerWithCopy
              title={t(TITLES_META)}
              disabled={isGeneralDisabledFields}
              hasCopyValue={true}
              onCopyCallback={() => onCopyValuesInOtherAds({ type: "titles" })}
              id={`${BASIC_PATH}.titles`}
              copyText={COPY_VALUES_IN_OTHER_ADS}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {titles?.map((title, index) => (
                  <TextFieldWithActions
                    key={title.id}
                    type="titles"
                    labelTitle={t(TITLE_PLACEHOLDER) + " " + (index + 1)}
                    name={`${BASIC_PATH}.titles.[${index}].value`}
                    disabled={isGeneralDisabledFields}
                    showAddIcon={
                      index === titles.length - 1 &&
                      index < maxTitlesPlatform - 1
                    }
                    showDeleteIcon={titles.length > 1}
                    platform={platform}
                    onChange={onGeneratePreview}
                    onAdd={() => onAddText({ type: "titles" })}
                    onRemove={() => onRemoveText({ type: "titles", index })}
                    maxLength={maxLengthTitle}
                    onGenerate={async () => {
                      await onGenerateText({ type: "titles", index });
                    }}
                    control={control}
                    errors={errors}
                  />
                ))}
              </Box>
            </CardContainerWithCopy>
          </Grid>
        )}

        <Grid item xs={6}>
          <CardContainerWithCopy
            title={t(HEADLINES)}
            disabled={isGeneralDisabledFields}
            hasCopyValue={true}
            onCopyCallback={() => onCopyValuesInOtherAds({ type: "headlines" })}
            id={`${BASIC_PATH}.headlines`}
            copyText={COPY_VALUES_IN_OTHER_ADS}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {headlines?.map((headline, index) => (
                <TextFieldWithActions
                  key={headline.id}
                  type="headlines"
                  labelTitle={t(HEADLINE_PLACEHOLDER) + " " + (index + 1)}
                  name={`${BASIC_PATH}.headlines.[${index}].value`}
                  disabled={isGeneralDisabledFields}
                  showAddIcon={
                    index === headlines.length - 1 &&
                    index < maxHeadlinesPlatform - 1
                  }
                  showDeleteIcon={headlines.length > 1}
                  platform={platform}
                  onChange={onGeneratePreview}
                  onAdd={() => onAddText({ type: "headlines" })}
                  onRemove={() => onRemoveText({ type: "headlines", index })}
                  maxLength={maxLengthHeadline}
                  onGenerate={async () => {
                    await onGenerateText({ type: "headlines", index });
                  }}
                  control={control}
                  errors={errors}
                />
              ))}
            </Box>
          </CardContainerWithCopy>
        </Grid>

        <Grid item xs={6}>
          <CardContainerWithCopy
            title={t(DESCRIPTIONS)}
            disabled={isGeneralDisabledFields}
            hasCopyValue={true}
            onCopyCallback={() =>
              onCopyValuesInOtherAds({ type: "descriptions" })
            }
            id={`${BASIC_PATH}.descriptions`}
            copyText={COPY_VALUES_IN_OTHER_ADS}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {descriptions?.map((description, index) => (
                <TextFieldWithActions
                  key={description.id}
                  type="descriptions"
                  labelTitle={t(DESCRIPTION_PLACEHOLDER) + " " + (index + 1)}
                  name={`${BASIC_PATH}.descriptions.[${index}].value`}
                  disabled={isGeneralDisabledFields}
                  showAddIcon={false}
                  showDeleteIcon={descriptions.length > 1}
                  platform={platform}
                  onChange={onGeneratePreview}
                  onAdd={() => onAddText({ type: "descriptions" })}
                  onRemove={() => onRemoveText({ type: "descriptions", index })}
                  maxLength={maxLengthDescription}
                  onGenerate={async () => {
                    await onGenerateText({ type: "descriptions", index });
                  }}
                  control={control}
                  errors={errors}
                />
              ))}
            </Box>
          </CardContainerWithCopy>
        </Grid>

        {!isSpecialAdWithoutCallToAction && isDestinationTypeAdWebsite && (
          <Grid item xs={6}>
            <CardContainer
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t(CALL_TO_ACTIONS_PLACEHOLDER)}
                  </Typography>
                </Box>
              }
            >
              <Grid container spacing={2}>
                {(() => {
                  const count = Math.max(1, callToActions?.length ?? 0);
                  return Array.from({ length: count }, (_, index) => (
                    <Grid item xs={12} key={index}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 0.5,
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <SelectCallToActionMeta
                            name={`${BASIC_PATH}.callToActions.${index}`}
                            label={CALL_TO_ACTION_PLACEHOLDER}
                            disabled={isGeneralDisabledFields}
                            fullWidth
                            objective={objective}
                            platform={platform}
                            destinationType={destinationTypeAdGroup}
                            control={control}
                            errors={errors}
                            required={index === 0}
                            excludeOptions={["BOOK_NOW"]}
                            disabledOptions={(callToActions || [])
                              .filter((_, i) => i !== index)
                              .filter(Boolean)}
                          />
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            pt: 1,
                            height: 60,
                          }}
                        >
                          {index === count - 1 &&
                            count < MAX_CALL_TO_ACTIONS && (
                              <IconButton
                                disabled={isGeneralDisabledFields}
                                size="small"
                                onClick={() => {
                                  const current = callToActions || [];
                                  setValue(
                                    `${BASIC_PATH}.callToActions`,
                                    [...current, ""],
                                    { shouldDirty: true, shouldValidate: true }
                                  );
                                  trigger(`${BASIC_PATH}.callToActions`);
                                  onGeneratePreview?.();
                                }}
                                sx={{ p: 0.4 }}
                              >
                                <AddIcon />
                              </IconButton>
                            )}
                          {count > 1 && (
                            <IconButton
                              disabled={isGeneralDisabledFields}
                              size="small"
                              onClick={() => {
                                const current = callToActions || [];
                                setValue(
                                  `${BASIC_PATH}.callToActions`,
                                  current.filter((_, i) => i !== index),
                                  { shouldDirty: true, shouldValidate: true }
                                );
                                trigger(`${BASIC_PATH}.callToActions`);
                                onGeneratePreview?.();
                              }}
                              sx={{ p: 0.4 }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  ));
                })()}
                <Grid item xs={12}>
                  <ClickableText
                    disabled={isGeneralDisabledFields}
                    onClick={() =>
                      onCopyValuesInOtherAds({ type: "callToActions" })
                    }
                  >
                    {t(COPY_VALUES_IN_OTHER_ADS)}
                  </ClickableText>
                </Grid>
              </Grid>
            </CardContainer>
          </Grid>
        )}
      </Grid>

      <Box sx={{ width: "100%", maxWidth: 400 }}>
        <CardContainer
          dynamicHeight={true}
          title={
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography variant="h6">{t(ADS_CAMPAIGN_PREVIEW)}</Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {!isGeneralDisabledFields && (
                  <EditAdvantageEnhances
                    disabled={isGeneralDisabledFields}
                    adID={adID}
                    adPathForm={BASIC_PATH}
                    onCallbackSelect={(newAdvantageEnhances) => {
                      setValue(
                        `${BASIC_PATH}.advantageEnhances`,
                        newAdvantageEnhances
                      );
                      onGeneratePreview();
                    }}
                  />
                )}
              </Box>
            </Box>
          }
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <AdPreviewMeta
              ref={refPreview}
              adID={adID}
              mediaUrls={mediaUrls}
              mediaData={mediaData}
              titles={titles}
              headlines={headlines}
              descriptions={descriptions}
              platform={platform}
              basicPath={BASIC_PATH}
              basicPathAdGroup={BASIC_PATH_AD_GROUP}
            />
          </Box>
        </CardContainer>
      </Box>
    </Box>
  );
};

export default AdAdDynamicContentMetaForm;
